/* eslint-disable no-nested-ternary */
import React from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import IcomoonReact from 'icomoon-react';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { BoxShadow } from '../../../../shared/GlobalStyles';

const AthleteName = styled('div')`
  color: #9E9E9E;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  margin-top: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AthleteStartData = styled('div')`
  align-items: center;
  color: #9E9E9E;
  display: flex;
  margin-left: 10px;
`;

const EndDataContainer = styled('div')`
  display: flex;
  justify-content: space-between;
`;

const EndDateData = styled('div')`
  align-items: center;
  display: flex;
  font-family: 'Nunito Sans';
  font-size: 41px;
  font-weight: 700;
  height: 55px;
`;

const ExerName = styled('div')`
  color: #9E9E9E;
  font-family: 'Nunito Sans';
  font-size: 12px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ExerTitleContainer = styled('div')`
`;

const FooterContainer = styled('div')`
  display: flex;
  font-family: 'Nunito Sans';
  font-size: 19px;
  font-weight: 400;
  justify-content: space-between;
`;

const GroupStartData = styled('div')`
  align-items: center;
  color: #9E9E9E;
  display: flex;
  margin-right: 10px;
`;

const MetricContainer = styled('div')`
  background-color: ${(props) => props.bgColor};
  border: ${(props) => (props.highlighted ? `solid 2px ${props.borderColor}` : null)};
  border-radius: 10px;
  box-shadow: ${BoxShadow};
  flex: 0 0 31%;
  height: 150px;
  margin: 10px;
  padding: 13px 10px 10px 10px;
`;

const MetricTitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MetricsContainer = styled('div')`
  display: flex;
  filter: ${(props) => (props.isBlurred ? 'blur(1.2px)' : null)};
  flex-wrap: wrap;
  justify-content: flex-start;
  margin: 0px 5px;
  padding-bottom: 10px;
`;

const Percentage = styled('div')`
  margin-left: 5px;
`;

const PercentageContainer = styled('div')`
  align-items: center;
  display: flex;
  svg {
    fill: ${(props) => (props.color ? props.color : '#9E9E9E')};
  }
`;

const StartDataContainer = styled('div')`
  display: flex;
`;

const StartDateData = styled('div')`
  align-items: center;
  color: #9E9E9E;
  display: flex;
`;

const TitleAthleteContainer = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  white-space: nowrap;
`;

const TitleContainer = styled('div')`
  display: flex;
  flex-direction: column;
  white-space: nowrap;
`;

const ExerciseMetricsContainers = () => {
  const {
    cardData,
    exerciseFiltersPerformance,
    exercises,
    selectedAthletePerformance,
    timingGateExercises,
  } = useSelector((state) => state.sportCoachDashboard.data);
  const { isAthleteSelectedPerformance } = useSelector((state) => state.sportCoachDashboard.ui);

  const theme = useTheme();

  const hasData = cardData.length > 0;
  const numPlaceholder = hasData ? '-' : '000';
  const percentPlaceholder = hasData ? '-%' : '0.00%';
  const colors = ['#1787FB', '#0DCC8A', '#FFCB47'];

  const getAthleteIndex = (cardIdx) => {
    if (!selectedAthletePerformance || !cardData[cardIdx]) return null;
    const idx = cardData[cardIdx].athleteData.findIndex(
      (athlete) => athlete.userId === selectedAthletePerformance.userId,
    );
    return idx !== -1 ? idx : null;
  };

  const getCardDataIndex = (exer) => {
    if (!cardData) return null;
    const idx = cardData.findIndex((item) => item.metricName === (exer.displayName ?? exer.name));
    return idx !== -1 ? idx : null;
  };

  const getColorForExercise = (exerId) => {
    const index = exerciseFiltersPerformance.findIndex((exercise) => exercise.id === exerId);
    return index !== -1 ? colors[index % colors.length] : null;
  };

  const getIconForPercentChange = (percentChange) => {
    if (percentChange > 0) return 'alt-up-arrow';
    if (percentChange < 0) return 'alt-down-arrow';
    return 'alt-right-arrow';
  };

  const renderPercentageChange = (percentChange, color) => (
    <PercentageContainer color={color || null}>
      <IcomoonReact
        icon={getIconForPercentChange(percentChange)}
        iconSet={iconSet}
        size={13}
      />
      <Percentage>
        {percentChange !== null && percentChange !== undefined
          ? `${percentChange}%`
          : percentPlaceholder}
      </Percentage>
    </PercentageContainer>
  );

  // Metric card with only group data
  const renderGroupOnlyCard = (exer, idx) => (
    <>
      <TitleContainer>
        <ExerName>{exer.displayName ?? exer.name}</ExerName>
        <MetricTitle>Group Average</MetricTitle>
      </TitleContainer>
      <EndDateData>
        {cardData[idx]?.newestAverage || numPlaceholder}
      </EndDateData>
      <FooterContainer>
        <StartDateData>
          {cardData[idx]?.oldestAverage || numPlaceholder}
        </StartDateData>
        {renderPercentageChange(cardData[idx]?.percentChange, cardData[idx]?.color)}
      </FooterContainer>
    </>
  );

  // Metric card split with athlete data
  const renderAthleteComparisonCard = (exer, idx, athleteIdx) => (
    <>
      <TitleAthleteContainer>
        <ExerTitleContainer>
          <ExerName>{exer.displayName ?? exer.name}</ExerName>
          <MetricTitle>Group Average</MetricTitle>
        </ExerTitleContainer>
        <AthleteName>
          {selectedAthletePerformance?.fullName}
        </AthleteName>
      </TitleAthleteContainer>
      <EndDataContainer>
        <EndDateData>
          {cardData[idx]?.newestAverage || numPlaceholder}
        </EndDateData>
        <EndDateData>
          {cardData[idx]?.athleteData[athleteIdx]?.newestMax || numPlaceholder}
        </EndDateData>
      </EndDataContainer>
      <FooterContainer>
        <StartDataContainer>
          <GroupStartData>
            {cardData[idx]?.oldestAverage || numPlaceholder}
          </GroupStartData>
          {renderPercentageChange(cardData[idx]?.percentChange, cardData[idx]?.color)}
        </StartDataContainer>
        <StartDataContainer>
          {renderPercentageChange(
            cardData[idx]?.athleteData[athleteIdx]?.percentChange,
            cardData[idx]?.athleteData[athleteIdx]?.color,
          )}
          <AthleteStartData>
            {cardData[idx]?.athleteData[athleteIdx]?.oldestMax || numPlaceholder}
          </AthleteStartData>
        </StartDataContainer>
      </FooterContainer>
    </>
  );

  const renderMetricCard = (exer) => {
    const idx = getCardDataIndex(exer);
    const athleteIdx = getAthleteIndex(idx);
    const highlightColor = getColorForExercise(exer.id);

    return (
      <MetricContainer
        key={exer.id}
        bgColor={theme.colors.background[0]}
        borderColor={highlightColor}
        highlighted={highlightColor !== null}
      >
        {isAthleteSelectedPerformance
          ? renderAthleteComparisonCard(exer, idx, athleteIdx)
          : renderGroupOnlyCard(exer, idx)}
      </MetricContainer>
    );
  };

  return (
    <MetricsContainer isBlurred={!hasData}>
      {exercises.length > 0 ? exercises.map(renderMetricCard) : null}
      {timingGateExercises.length > 0 ? timingGateExercises.map(renderMetricCard) : null}
    </MetricsContainer>
  );
};

export default ExerciseMetricsContainers;
